import { PlayCircleOutline } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import { useListContext, useTranslate } from 'react-admin';

import { UsageLogEntry } from 'types/LiveLogs';
import { formatClockTime, formatDuration, LIVE_LOGS_I18N, readRunningSession } from './liveLogs';
import { runningTint } from './logCellSx';

const RunningSessionDetails = ({ running }: { running: UsageLogEntry }) => {
  const translate = useTranslate();
  const startedAt = running.start_time ?? running.timestamp;
  // Computed at render, so it moves on each refetch rather than ticking every second.
  const elapsedSeconds = startedAt === null ? Number.NaN : (Date.now() - Date.parse(startedAt)) / 1000;

  const details = [
    translate(`${LIVE_LOGS_I18N}.running.started`, { time: formatClockTime(startedAt) }),
    Number.isFinite(elapsedSeconds)
      ? translate(`${LIVE_LOGS_I18N}.running.elapsed`, { duration: formatDuration(elapsedSeconds) })
      : null,
    running.software_version,
  ].filter((part): part is string => !!part);

  return (
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {details.join(' · ')}
    </Typography>
  );
};

/**
 * The usagelog's running session, from the response `meta.running`: it does not depend on the
 * range or the page. Rendered inside the List's content, so the error state replaces it.
 */
export const RunningSessionCard = () => {
  const { meta } = useListContext<UsageLogEntry>();
  const translate = useTranslate();
  const running = readRunningSession(meta);

  if (running === undefined) {
    return null;
  }

  if (running === null) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
        {translate(`${LIVE_LOGS_I18N}.running.none`)}
      </Typography>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, m: 2, bgcolor: runningTint, borderColor: 'primary.main', color: 'secondary.main' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlayCircleOutline fontSize="small" />
        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
          {translate(`${LIVE_LOGS_I18N}.running.title`)}
        </Typography>
      </Box>
      <Typography variant="h6" component="p" sx={{ my: 0.5 }}>
        {running.game ?? '-'}
      </Typography>
      <RunningSessionDetails running={running} />
    </Paper>
  );
};
